import { useState, useEffect, useRef } from 'react';
import * as Tone from 'tone';
import './App.css';
import AudioMixer from './components/AudioMixer';
import './styles/WelcomePage.css';
import './styles/Transitions.css';
import './styles/MPCBeatPad.css';
import './styles/TypewriterText.css';
import ObjectiveCallout from './components/ObjectiveCallout';
import TypewriterText from './components/TypewriterText';
import MPCBeatPad from './components/MPCBeatPad';
import img1 from './assets/1.jpg';
import img2 from './assets/2.jpg';
import img3 from './assets/3.jpg';
import img4 from './assets/4.jpg';
import img5 from './assets/5.jpg';
import img6 from './assets/6.jpg';
import img7 from './assets/7.jpg';
import { audioTracksData } from './data/audioTracks';
import RingerPopup from './components/RingerPopup';

function App() {
  const [isStarted, setIsStarted] = useState(false);
  const [showCongrats, setShowCongrats] = useState(false);
  const [congratsDismissed, setCongratsDismissed] = useState(false);
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [songEnded, setSongEnded] = useState(false);
  const [objectiveStatus, setObjectiveStatus] = useState("yellow");
  const silentAudioRef = useRef(null);
  const [audioTracks, setAudioTracks] = useState(audioTracksData);
  const [currentDescription, setCurrentDescription] = useState(audioTracksData[0].description);
  const [currentCallout, setCurrentCallout] = useState(audioTracksData[0].callout);
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);
  const [showMobileArrow, setShowMobileArrow] = useState(false);
  const mixerScrollRef = useRef(null);
  const vocalsMutedByPad = useRef(false);

  // Function to restart the song
  const handlePlayAgain = async () => {
    try {
      // Resume audio context if needed
      if (Tone.context.state !== 'running') {
        await Tone.context.resume();
      }
      
      // Stop and reset all players
      audioTracks.forEach(track => {
        if (track.player) {
          track.player.stop();
          track.player.start();
        }
      });
      
      // Reset Transport if needed
      Tone.Transport.stop();
      Tone.Transport.start();
      
      setIsPlaying(true);
      setSongEnded(false);
    } catch (error) {
      console.error('Error restarting audio:', error);
    }
  };

  // Use effect to check if the song has ended
  useEffect(() => {
    if (isStarted && isPlaying && audioTracks.length > 0 && audioTracks[0].player) {
      // Check the state of the first track's player
      const checkPlaybackState = () => {
        // If all players have finished, set songEnded to true
        const allEnded = audioTracks.every(track => 
          track.player && track.player.state === 'stopped'
        );
        
        if (allEnded && isPlaying) {
          setSongEnded(true);
          setIsPlaying(false);
        }
      };
      
      // Check every second
      const interval = setInterval(checkPlaybackState, 1000);
      
      return () => clearInterval(interval);
    }
  }, [isStarted, isPlaying, audioTracks]);

  // Add scroll listener to hide indicator when user scrolls to the end
  useEffect(() => {
    const handleScroll = () => {
      if (mixerScrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = mixerScrollRef.current;
        // Hide indicator when scrolled near the end (within 20px of the end)
        if (scrollWidth - (scrollLeft + clientWidth) < 20) {
          setShowScrollIndicator(false);
        }
        // Hide mobile arrow once scrolled past 60% of the scrollable area
        if (scrollLeft / (scrollWidth - clientWidth) > 0.6) {
          setShowMobileArrow(false);
        }
      }
    };

    const scrollContainer = mixerScrollRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Show mobile arrow when beat pad appears
  useEffect(() => {
    if (showCongrats && isStarted) {
      setShowMobileArrow(true);
    }
  }, [showCongrats, isStarted]);

  const handleStart = async () => {
    try {
      // First, resume the audio context
      await Tone.context.resume();
      
      // Then start Tone.js
      await Tone.start();
      setIsStarted(true);
      
      // Initialize players only after audio context is ready
      const updatedTracks = [...audioTracks];
      
      const playerPromises = updatedTracks.map(track => {
        return new Promise((resolve) => {
          const player = new Tone.Player({
            url: track.file,
            loop: false,
            autostart: false,
            onload: () => resolve(player)
          }).toDestination();
          
          player.volume.value = Tone.gainToDb(track.volume);
          return player;
        });
      });
      
      const loadedPlayers = await Promise.all(playerPromises);
      
      for (let i = 0; i < updatedTracks.length; i++) {
        updatedTracks[i].player = loadedPlayers[i];
      }
      
      setAudioTracks(updatedTracks);
      
      // Make sure context is in running state before starting playback
      if (Tone.context.state !== 'running') {
        await Tone.context.resume();
      }
      
      Tone.Transport.start();
      loadedPlayers.forEach(player => player.start());
      setIsPlaying(true);
      
    } catch (error) {
      console.error('Error starting audio:', error);
    }
  };

  // Function to reveal the next track
  const revealNextTrack = () => {

    // animate the callout
    
    // fade it out
    // reveal the next track
    const updatedTracks = [...audioTracks];
    const nextTrackIndex = updatedTracks.findIndex(track => !track.visible);
    
    if (nextTrackIndex !== -1) {
      // Add a delay before revealing the next track
      setTimeout(() => {
        updatedTracks[nextTrackIndex].visible = true;
        setAudioTracks(updatedTracks);

        // Add a delay before updating description and callout
        setTimeout(() => {
          setObjectiveStatus("yellow");
          setCurrentDescription(audioTracksData[nextTrackIndex].description);
          setCurrentCallout(audioTracksData[nextTrackIndex].callout);
        }, 800); // Delay description update by 400ms after track reveal
      }, 0); // Delay track reveal by 800ms
    } else {
      // All tracks are visible, show congratulations
      if(!congratsDismissed) {
        setShowCongrats(true);
      }
    }
  };

  // Function to check if all tracks have volume above threshold
  useEffect(() => {
    if (isStarted) {
      const allTracksAboveThreshold = audioTracks.every(track => track.volume >= 0.5);
      if (allTracksAboveThreshold && !showCongrats) {
        setShowCongrats(true);
      }
    }
  }, [audioTracks, isStarted, showCongrats]);

  useEffect(() => {
    if (congratsDismissed) {
      setShowCongrats(false);
    }
  }, [congratsDismissed]);

  // Function to handle volume change
  const handleVolumeChange = (id, newVolume) => {
    const updatedTracks = audioTracks.map(track => {
      if (track.id === id) {
        // Update the player volume
        if (track.player) {
          track.player.volume.value = Tone.gainToDb(newVolume);
        }
        
        return { ...track, volume: newVolume };
      }
      return track;
    });
    
    setAudioTracks(updatedTracks);
    
    // Check if we should reveal the next track based on the current track's volume
    const currentTrack = updatedTracks.find(track => track.id === id);
    const visibleTracks = updatedTracks.filter(track => track.visible);
    const lastVisibleTrack = visibleTracks[visibleTracks.length - 1];
    
    if (currentTrack && lastVisibleTrack && currentTrack.id === lastVisibleTrack.id && newVolume >= 0.5) {
      setObjectiveStatus("green");
      revealNextTrack();
    }
  };

  // Function to handle mute toggle
  const handleMuteToggle = (id) => {
    const updatedTracks = audioTracks.map(track => {
      if (track.id === id) {
        const newMuteState = !track.mute;
        
        if (track.player) {
          if (newMuteState) {
            track.player.volume.value = -Infinity;
          } else {
            track.player.volume.value = Tone.gainToDb(track.volume);
          }
        }
        
        return { ...track, mute: newMuteState, solo: false };
      }
      return track;
    });
    
    setAudioTracks(updatedTracks);
  };

  // Function to handle solo toggle
  const handleSoloToggle = (id) => {
    const updatedTracks = audioTracks.map(track => {
      if (track.id === id) {
        // Toggle solo for this track
        return { ...track, solo: !track.solo };
      }
      return track;
    });
    
    const hasSoloTrack = updatedTracks.some(track => track.solo);
    
    // Update player volumes based on solo state
    updatedTracks.forEach(track => {
      if (track.player) {
        if (hasSoloTrack) {
          // If any track is soloed, mute all non-soloed tracks
          if (track.solo) {
            track.player.volume.value = Tone.gainToDb(track.volume);
          } else {
            track.player.volume.value = -Infinity;
          }
        } else {
          // If no track is soloed, respect the mute state
          if (track.mute) {
            track.player.volume.value = -Infinity;
          } else {
            track.player.volume.value = Tone.gainToDb(track.volume);
          }
        }
      }
    });
    
    setAudioTracks(updatedTracks);
  };
  

  const handlePadHit = () => {
    if (vocalsMutedByPad.current) return;
    vocalsMutedByPad.current = true;
    // Mute the vocal track
    const vocalTrack = audioTracks.find(t => t.id === 'acapella');
    if (vocalTrack && vocalTrack.player) {
      vocalTrack.player.volume.value = -Infinity;
    }
    setAudioTracks(prev => prev.map(t =>
      t.id === 'acapella' ? { ...t, mute: true } : t
    ));
  };

  let congrats;


  let description;

  if (isStarted && !showCongrats) {
    description = (
      <>
        <h3 className="description">
          <TypewriterText 
            text={currentDescription} 
            speed={30}
            key={currentDescription}
          />
        </h3>
        <ObjectiveCallout text={currentCallout} status={objectiveStatus}/>
      </>
    );
    
  } else if (isStarted && showCongrats) {
    description = (
      <>
        <h3 className="description">
          <TypewriterText 
            text="Aaaaaand that's it – hope you enjoyed :) Feel free to continue messing with knobs and making it your own! I placed a beat pad on the right that plays lil vocal chops, go stupid on it. The full remix is linked below. love, <a href='https://adhiv.com' target='_blank' rel='noopener noreferrer'>adhiv</a>"
            speed={30}
            key="congrats"
          />
        </h3>
        <h4 className="callout">
          <div className="social-links">
            <a href="https://soundcloud.com/dharbeats/alaska-maggie-rogers-adhiv-remix" className="social-link soundcloud" target="_blank" rel="noopener noreferrer">
              <span className="social-icon soundcloud-icon"></span>
              <span className="social-text">soundcloud</span>
            </a>
            <a href="https://www.youtube.com/watch?v=ZH6k83DRp7E" className="social-link youtube" target="_blank" rel="noopener noreferrer">
              <span className="social-icon youtube-icon"></span>
              <span className="social-text">youtube</span>
            </a>
            {songEnded && (
              <button 
                className="social-link play-again" 
                onClick={handlePlayAgain}
              >
                <span className="social-icon play-again-icon"></span>
                <span className="social-text">play again</span>
              </button>
            )}
             {/* Visual indicator to show there's more to scroll */}
            {isStarted && showCongrats && showScrollIndicator && (
              <div className="scroll-indicator">→</div>
            )}
                      
          </div>
        </h4>
      </>
    );
  }
  else {
    description = (
      <>
        <h3 className="description">
          <TypewriterText 
            text="One of my favorite parts about music is getting to dissect the layers that build up a song. I made this website to showcase how beautiful music can be piece by piece; let's get into it:"
            speed={10}
            key="initial"
          />
        </h3>
        <h4 className="callout">
          <button className="start-button" onClick={handleStart}>start ▶</button>
        </h4>
      </>
    );
  }

  return (
    <div className="App">
        <>
          <div className="container">
              <div className="text-content INTRO">
                <div className="text-fr">
                <h1 className="title">remix my remix</h1>
                <h2 className="subtitle">Alaska, Maggie Rogers (adhiv's remix)</h2>

               {description}
                 
               </div>
              </div>
              
        <div className="moodboard INTRO">
          <img src={img5} alt="Moodboard visual 2" className="moodboard-img img5" />
          <img src={img6} alt="Moodboard visual 3" className="moodboard-img img6" /> 
          <img src={img7} alt="Moodboard visual 3" className="moodboard-img img7" /> 
        </div>
          
          {congrats}
        </div>
        <div className="mixer-scroll" ref={mixerScrollRef}>
                  <div className="mixer-components">
                    <AudioMixer 
                      tracks={audioTracks} 
                      onVolumeChange={handleVolumeChange}
                      onMuteToggle={handleMuteToggle}
                      onSoloToggle={handleSoloToggle}
                      isPlaying={isPlaying}
                      currentDescription={currentDescription}
                      currentCallout={currentCallout}
                    />

                    {/* Show MPC Beat Pad after congratulations */}
                    {isStarted && showCongrats && (
                      <div className="mpc-wrapper">
                        <MPCBeatPad onPadHit={handlePadHit} />
                      </div>
                    )}
                  </div>
               
        </div>
          
        
        </>
        {showMobileArrow && (
          <div className="mobile-scroll-arrow">→</div>
        )}
        <RingerPopup />
    </div>
  );
}

export default App;